import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isMobileBreakpoint, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { setSealOverlayState, waitForSealOverlayMotion } from '@theme/seal-overlay-lifecycle';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #motionSequence = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#handleMobileAccordionClick, { capture: true });
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#handleMobileAccordionClick, { capture: true });
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    const { details } = this.refs;
    const state = details.getAttribute('data-seal-overlay-state');

    return details.classList.contains('menu-open') || state === 'opening' || state === 'open';
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    event.preventDefault();
    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    event?.preventDefault();

    if (details.open && details.classList.contains('menu-open')) return;

    const motionSequence = ++this.#motionSequence;
    details.open = true;
    summary.setAttribute('aria-expanded', 'true');
    setSealOverlayState(details, 'opening');

    if (details === this.refs.details) {
      document.documentElement.setAttribute('scroll-lock', '');
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (motionSequence !== this.#motionSequence) return;

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      this.#afterDrawerMotion(drawer || details, () => {
        if (motionSequence !== this.#motionSequence || !details.classList.contains('menu-open')) return;

        setSealOverlayState(details, 'open');
        trapFocus(details);
      });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');
    const state = details.getAttribute('data-seal-overlay-state');

    if (!summary || state === 'closing' || (!details.open && !details.classList.contains('menu-open'))) return;

    const motionSequence = ++this.#motionSequence;
    const activeElement = document.activeElement;
    const shouldRestoreFocus = activeElement instanceof Node && details.contains(activeElement);

    summary.setAttribute('aria-expanded', 'false');
    setSealOverlayState(details, 'closing');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (details === this.refs.details) {
      document.documentElement.removeAttribute('scroll-lock');
      removeTrapFocus();
    } else {
      trapFocus(this.refs.details);
    }

    if (shouldRestoreFocus) {
      summary.focus({ preventScroll: true });
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    this.#afterDrawerMotion(drawer || details, () => {
      if (motionSequence !== this.#motionSequence) return;

      reset(details);
      setSealOverlayState(details, 'closed');
      if (details === this.refs.details) {
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach((openDetailsElement) => {
          reset(openDetailsElement);
          setSealOverlayState(openDetailsElement, 'closed');
        });
      }
    });
  }

  /**
   * Runs a drawer motion callback after the currently painted transition.
   * @param {Element} element
   * @param {() => void} callback
   */
  #afterDrawerMotion(element, callback) {
    waitForSealOverlayMotion(element).then(callback);
  }

  /**
   * Owns the mobile menu accordion state explicitly instead of relying on the
   * delayed native summary default action in iOS Safari.
   *
   * @param {MouseEvent} event
   */
  #handleMobileAccordionClick = (event) => {
    if (!isMobileBreakpoint() || !(event.target instanceof Element)) return;

    const summary = event.target.closest('summary');
    const details = summary?.parentElement;
    const accordion = summary?.closest('accordion-custom');

    if (
      !summary ||
      !(details instanceof HTMLDetailsElement) ||
      !(accordion instanceof HTMLElement) ||
      accordion.closest('header-drawer') !== this
    ) {
      return;
    }

    event.preventDefault();
    details.open = !details.open;
    summary.setAttribute('aria-expanded', String(details.open));
  };

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        content.forEach((element) => {
          if (element instanceof HTMLElement) {
            element.classList.remove('details-content--no-animation');
          }
        });
      });
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
